import React, { useMemo } from 'react';
import { HTTPResponseTable } from '../HTTPResponseTable';
import { SyntaxDisplay } from '../SyntaxDisplay/index';
import { SectionContainer } from '../../../SectionContainer';
import style from './RESTOperationView.scss';
import { Text } from '../../../PageElements/Text';
import { LazyLoader } from '../../../LazyLoader/index';
import { NestedTable } from '../../NestedTable';
import { useRestPlaygroundModal } from '../../../../customHooks/useRestPlaygroundModal';
import { shouldOpenPlayground } from './RESTPlaygroundContainer/utils';
import { useLocation } from 'react-router';
import { getName } from '../../../../../utils/treeVisitors/helpers';
import { capitalizeFirst } from '../../../../../utils/stringUtils';
import { isClientSide } from '../../../../../common/utils';
import get from 'lodash/get';
import { getParamWithRequiredField } from '../utils';

const RESTPlaygroundContainer = React.lazy(() =>
  import('./RESTPlaygroundContainer'),
);

const TablesParamsTypes = ['path', 'body', 'query'];

export const RESTOperationView = ({ operation, artifactId, prefix }) => {
  const location = useLocation();

  const shouldOpenPlaygroundFromShareableLink = shouldOpenPlayground(
    operation?.urlFormat,
    location,
  );

  const { onOpenPlaygroundClick, ...restPlaygroundData } =
    useRestPlaygroundModal({
      shouldOpenPlaygroundFromShareableLink,
      artifactId,
      operation,
    });

  const paramsTable = useMemo(() => {
    return generateParamTables();
  }, [operation]);

  return (
    <React.Fragment>
      <SectionContainer>
        <div className={style.endpoint}>
          <SyntaxDisplay
            operation={operation}
            artifactId={artifactId}
            onOpenPlaygroundClick={onOpenPlaygroundClick}
          />
          {isClientSide() && (
            <LazyLoader
              lazyComponent={RESTPlaygroundContainer}
              data={operation}
              artifactId={artifactId}
              {...restPlaygroundData}
            />
          )}
        </div>
      </SectionContainer>
      {paramsTable}
      {operation?.ret && operation?.ret?.type !== 'void' && (
        <HTTPResponseTable
          response={operation?.ret}
          prefix={prefix}
          pageName={getName(operation)}
        />
      )}
    </React.Fragment>
  );

  function generateParamTables() {
    const tablesParams = generateTableDataForSpecificParam();

    const doesApiHasParams = Object.values(tablesParams).some(
      (table) => table.length > 0,
    );

    return doesApiHasParams ? (
      Object.keys(tablesParams).map(
        (paramType) =>
          tablesParams[paramType]?.length > 0 && (
            <SectionContainer key={paramType}>
              <NestedTable
                tableDepth={0}
                params={tablesParams[paramType]}
                title={capitalizeFirst(`${paramType} Params`)}
                hasRest={get(operation, 'extra.hasRest', true)}
                isRequest={true}
              />
            </SectionContainer>
          ),
      )
    ) : (
      <SectionContainer title="Request">
        <Text size="medium" weight="thin">
          This endpoint does not take any parameters
        </Text>
      </SectionContainer>
    );
  }

  function generateTableDataForSpecificParam() {
    return (operation?.params || []).reduce((acc, param) => {
      const paramType = param?.in?.toString().toLowerCase();

      if (TablesParamsTypes.every((tableParam) => tableParam !== paramType)) {
        return acc;
      }

      if (!acc[paramType]) {
        acc[paramType] = [];
      }

      const paramWithRequiredField = getParamWithRequiredField({
        param,
        operationRequiredFields: operation.requiredFields,
      });

      acc[paramType].push(paramWithRequiredField);

      return acc;
    }, {});
  }
};
